import { get, first, isEqual, has, pick } from 'lodash';
import uuid from 'node-uuid';

import { isChapsEnabled } from '../../../states/capabilities/selectors';
import { formatSortCode } from '../../../utils/accountUtils';
import {
	TYPE_BACS_CREDIT,
	TYPE_BACS_DEBIT,
	TYPE_FASTER_PAYMENTS,
	TYPE_IFT_PAYEE,
	TYPE_DOMESTIC_PAYEE,
	TYPE_CHAPS,
	TYPE_IFT,
	payeeTypes,
	swiftFormat,
} from '../../../config';
import { getPayeeType } from '../../../managePayee/states/selectors';
import { getPayments } from '../../../states/bacsCredit/selectors';
import { getPaymentsList } from '../../../states/payments/getPaymentsList';

import { getChapsFromAccount, getChapsPaymentsList } from '../../../states/chaps/selectors';
import { getPaymentTypeOrPayeeType } from '../../../states/modes/selectors';
import { getPayeeById } from '../../../states/commonSelectors/selectors';

import { getBacsPaymentIds } from '../../../states/bacsCredit/selectors';

export const getLinkAccountDetails = ({ addPayeeForm: { linkAccount }, accounts, page: { name } }) => {
	if (name.includes('Payee/Edit')) {
		return { id: linkAccount };
	}
	const { id, sortCode, accountNumber } = accounts.find((account) => account.id === linkAccount) || {};
	return { id, sortCode, accountNumber };
};
export const getSourceAccountId = (state) => {
	const {
		managePayee: { payeeType },
		accounts,
	} = state;
	const accountCapability =
		payeeType === TYPE_IFT_PAYEE ? '/account/payments/ift/out' : '/account/payments/uk/default/out';
	const sourceAccount = accounts.find((account) => account.actionsAvailable[accountCapability] === true);
	return sourceAccount ? sourceAccount.id : '';
};
export const getAddPayeeFormDetails = (state) => state.addPayeeForm;
export const getErrors = ({ addPayeeForm: { showError, errorCode } }) => ({ showError, errorCode });

export const getEditPayeeDetails = (state) => {
	const {
		managePayee: { selectedPayee },
	} = state;
	if (selectedPayee === null) return {};
	const {
		to_account,
		display_name,
		account,
		reference,
		type,
		address,
		reference_high_value,
		bank,
		confirmation_of_payee,
		cop_account_name,
		cop_account_type,
	} = selectedPayee;
	const iban = get(to_account, 'iban');
	const internationalAccountNumber = get(to_account, 'account_number');
	const bickOrSwift = get(to_account, 'bic');
	const payeeAccountType = iban ? 'iban' : 'accono';
	const noBicOrSwift = internationalAccountNumber && !bickOrSwift;
	const payeeName = get(to_account, 'name');
	if (type === payeeTypes.INTERNATIONAL) {
		return {
			linkAccount: get(account, 'id'),
			internationalPayeeName: get(to_account, 'name'),
			singleUse: false,
			displayName: display_name || '',
			payeeAddressLine1: get(address, 'address_line_1'),
			payeeAddressLine2: get(address, 'address_line_2'),
			payeeCity: get(address, 'city'),
			payeePostalCode: get(address, 'postal_code'),
			payeeCountry: get(address, 'country'),
			payeeAccountType,
			iban,
			internationalAccountNumber,
			paymentReason: reference_high_value,
			bickOrSwift,
			clearingCode: get(to_account, 'clearing_code'),
			noBicOrSwift,
			bankAddressLine1: get(bank, 'address.address_line_1'),
			bankAddressLine2: get(bank, 'address.address_line_2'),
			bankCity: get(bank, 'address.city'),
			bankPostalCode: get(bank, 'address.postal_code'),
			bankCountry: get(bank, 'address.country'),
			bankName: get(bank, 'name'),
			type: TYPE_IFT_PAYEE,
		};
	}
	return {
		name: cop_account_name || payeeName || '',
		displayName: display_name || '',
		sortCode: formatSortCode(get(to_account, 'sort_code')),
		accountNumber: get(to_account, 'account_number'),
		linkAccount: get(account, 'id'),
		singleUse: false,
		type: TYPE_DOMESTIC_PAYEE,
		chapsPaymentsEnable: type === payeeTypes.CHAPS,
		chapsReference: reference_high_value || '',
		payeeAddressLine1: get(address, 'address_line_1'),
		payeeAddressLine2: get(address, 'address_line_2'),
		payeeCity: get(address, 'city'),
		payeePostalCode: get(address, 'postal_code'),
		payeeCountry: get(address, 'country'),
		payeeCopDetails: confirmation_of_payee || '',
		copType: cop_account_type,
		reference,
	};
};
export const getEditPayeeButtonStatus = (state) => {
	const {
		addPayeeForm: { showError, errorCode, ...editPayeeDetails },
	} = state;
	const payeeDetails = getEditPayeeDetails(state);
	editPayeeDetails.sortCode = formatSortCode(editPayeeDetails.sortCode);
	payeeDetails.sortCode = formatSortCode(payeeDetails.sortCode);
	return isEqual(editPayeeDetails, payeeDetails);
};
const comparisonKeys = [
	'chapsReference',
	'payeeAddressLine1',
	'payeeAddressLine2',
	'payeeCity',
	'payeePostalCode',
	'payeeCountry',
];

export function getEditChapsButtonStatus(state) {
	return isEqual(pick(getEditPayeeDetails(state), comparisonKeys), pick(state.addPayeeForm, comparisonKeys));
}
export const isPaymentFlow = ({ page: { backPage } }) => backPage && backPage[0] !== 'ManageYourPayee';
export const getFromAccountId = ({ page: { sharedData } }) => sharedData && sharedData.fromAccountId;
export const getAddMultiplePaymentsFlag = ({ page: { sharedData } }) => sharedData && sharedData.canAddMultiplePayments;
export const getSelectedPaymentId = ({ page: { sharedData } }) => sharedData && sharedData.selectedPaymentId;

export const showSingleUseBox = (state, isEditPayee) => {
	if (isEditPayee) return !isEditPayee;
	return isPaymentFlow(state);
};

export const getPaymentDetails = ({
	paymentTypeOrPayeeType,
	addPayeeForm,
	response,
	chapsFromAccount,
	bacsCreditId,
}) => {
	const { linkAccount, reference, chapsReference, otherPaymentReference } = addPayeeForm || {};
	switch (paymentTypeOrPayeeType) {
		case TYPE_BACS_CREDIT:
			return {
				id: bacsCreditId || uuid.v1(),
				toAccount: response.id,
				reference: chapsReference || reference || otherPaymentReference,
			};
		case TYPE_BACS_DEBIT:
			return {
				id: uuid.v1(),
				fromAccount: response.id,
				reference: chapsReference || reference || otherPaymentReference,
			};
		case TYPE_FASTER_PAYMENTS:
			return {
				fromAccount: linkAccount,
				toAccount: response.id,
				reference: chapsReference || reference || otherPaymentReference,
			};
		case TYPE_CHAPS:
			return {
				fromAccount: linkAccount ? linkAccount : chapsFromAccount,
				toAccount: response.id,
				reference: chapsReference || otherPaymentReference,
			};
		case TYPE_IFT:
			return {
				fromAccount: linkAccount,
				toAccounts: [response.id],
			};
		default:
			return {};
	}
};

export const isFromChapsPayment = (paymentTypeOrPayeeType) => paymentTypeOrPayeeType === TYPE_CHAPS;
export const showChapsCheckBox = (state, isFromChapsPayment) => isChapsEnabled(state) && !isFromChapsPayment;
export const getIsPayeeDeleted = (state) => state.addPayeeForm.isPayeeDeleted;

const getIftPayeeSensitiveValues = (payee) => {
	const { accountNumber = '', iban = '', bic = '', clearingCode = '' } = payee;
	return {
		iban,
		internationalAccountNumber: accountNumber,
		bickOrSwift: bic,
		clearingCode,
	};
};
const getDomesticPayeeSensitiveValues = (payee) => {
	const { sortCode = '', accountNumber = '' } = payee;
	return {
		sortCode,
		accountNumber,
	};
};

export const showFraudAwarenessWarning = (isEditPayee, state) => {
	const payeeType = getPayeeType(state);
	if (isEditPayee) {
		if (payeeType === TYPE_IFT_PAYEE) {
			const {
				addPayeeForm: { iban = '', bickOrSwift = '', clearingCode = '', internationalAccountNumber = '' },
			} = state;
			const payee = get(state, 'managePayee.selectedPayee', {});
			const updatedPayee = { iban, bickOrSwift, clearingCode, internationalAccountNumber };
			return !isEqual(getIftPayeeSensitiveValues(payee), updatedPayee);
		}
		if (payeeType === TYPE_DOMESTIC_PAYEE) {
			const {
				addPayeeForm: { sortCode = '', accountNumber = '' },
			} = state;
			const payee = get(state, 'managePayee.selectedPayee', {});
			const updatedPayee = { sortCode: sortCode.replace(/-/g, '') || '', accountNumber };

			return !isEqual(getDomesticPayeeSensitiveValues(payee), updatedPayee);
		}
	}
	return true;
};

export const isValidChapsCharsFormat = (state, noChapsCheck) => {
	const {
		chapsPaymentsEnable,
		name,
		chapsReference,
		payeeAddressLine1,
		payeeAddressLine2,
		payeeCity,
		payeePostalCode,
	} = state.addPayeeForm;

	if (chapsPaymentsEnable || noChapsCheck) {
		const enforceFieldsList = [name, chapsReference, payeeAddressLine1, payeeAddressLine2, payeeCity, payeePostalCode];
		const isValid = !enforceFieldsList.join('').match(swiftFormat);
		return isValid;
	}
	return true;
};
export const isPayeeNameValid = (state) => {
	const { chapsPaymentsEnable, name } = state.addPayeeForm;
	const nameRegx = /[^0-9A-Za-z\s&'().,/ -]/g;
	if (!chapsPaymentsEnable) {
		const isValid = name.match(nameRegx);
		if (isValid !== null) {
			return isValid.length === 0;
		}
		return true;
	}
	return true;
};

export const getAccountNameServicesError = (state) => get(state, 'api.accountNameServicesError');

export const getAddPayeePaymentDetails = (state, response) => {
	const paymentTypeOrPayeeType = getPaymentTypeOrPayeeType(state);
	const addPayeeForm = getAddPayeeFormDetails(state);
	const { linkAccount, reference, chapsReference, otherPaymentReference } = addPayeeForm || {};
	const hasAddPayeeFormRef = reference || chapsReference || otherPaymentReference;
	const chapsFromAccount = getChapsFromAccount(state);

	switch (paymentTypeOrPayeeType) {
		case TYPE_FASTER_PAYMENTS: {
			const paymentsList = getPaymentsList(state);
			const payment = {
				...paymentsList[0],
				toAccount: get(response, 'id'),
				...(hasAddPayeeFormRef && { reference: chapsReference || reference || otherPaymentReference }),
				...(linkAccount && { fromAccount: linkAccount }),
			};
			return payment;
		}
		case TYPE_CHAPS: {
			const paymentsList = getChapsPaymentsList(state);
			const { id } = response;
			const payment = {
				...paymentsList[0],
				toAccount: id,
				...(hasAddPayeeFormRef && { reference: chapsReference || reference || otherPaymentReference }),
				...((linkAccount || chapsFromAccount) && { fromAccount: linkAccount || chapsFromAccount }),
			};
			return payment;
		}
		case TYPE_BACS_CREDIT: {
			const firstPayment = first(getPayments(state)) || {};

			const oldPayee = getPayeeById(state, firstPayment.toAccount);
			const shouldUpdate = !has(oldPayee, 'to_account');

			const bacsCreditId = shouldUpdate ? getBacsPaymentIds(state)?.[0] : response.id;

			return getPaymentDetails({ paymentTypeOrPayeeType, addPayeeForm, response, chapsFromAccount, bacsCreditId });
		}
		default: {
			return getPaymentDetails({ paymentTypeOrPayeeType, addPayeeForm, response, chapsFromAccount });
		}
	}
};
